import React from 'react'
import {
  Document, Page, Text, View, StyleSheet
} from '@react-pdf/renderer'
import { ClientData } from '../types/ClientData'

const NAVY = '#0f1e35'
const GOLD = '#C9A84C'
const WHITE = '#ffffff'
const LIGHT_GREY = '#f1f5f9'
const MID_GREY = '#64748b'
const GREEN = '#166534'

const styles = StyleSheet.create({
  page: {
    backgroundColor: WHITE,
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1e293b',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: NAVY,
  },
  headerLeft: { flex: 1 },
  headerRight: { alignItems: 'flex-end' },
  firmName: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: NAVY },
  docTitle: { fontSize: 10, color: MID_GREY, marginTop: 2 },
  clientName: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY },
  clientDate: { fontSize: 9, color: MID_GREY, marginTop: 2 },
  section: { marginBottom: 18 },
  sectionTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: MID_GREY,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  freedomBox: {
    backgroundColor: NAVY,
    borderRadius: 6,
    padding: 16,
    marginBottom: 20,
    alignItems: 'center',
  },
  freedomLabel: {
    fontSize: 8,
    color: '#94a3b8',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  freedomNumber: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    color: GOLD,
  },
  freedomSub: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 4,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metricCard: {
    width: '31%',
    backgroundColor: LIGHT_GREY,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
  },
  metricLabel: { fontSize: 8, color: MID_GREY, marginBottom: 4 },
  metricValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: NAVY },
  metricSub: { fontSize: 8, color: MID_GREY, marginTop: 2 },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  actionLabel: { fontSize: 10, color: '#1e293b', flex: 1 },
  actionValue: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: NAVY },
  soBox: {
    backgroundColor: '#eff6ff',
    borderRadius: 4,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#1e40af',
  },
  soTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1e40af', marginBottom: 6 },
  soRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  soLabel: { fontSize: 9, color: MID_GREY },
  soValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1e293b' },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 8, color: MID_GREY },
  disclaimer: {
    fontSize: 7,
    color: '#94a3b8',
    marginTop: 4,
    lineHeight: 1.4,
  },
})

interface Props {
  data: ClientData
}

export function Session1SummaryPDF({ data: d }: Props) {
  const name = d.clientName || 'Client'
  const freedomAge = d.targetFreedomAge || 55
  const freedomNumberMonthly = d.freedomNumberInflated || 0

  const fmt = (n: number) => `£${n.toLocaleString()}`

  // Pension contribution (plan)
  const pensionMonthly = d.planPensionFromDividendReduction || 0
  const dividendReduction = d.planDividendReduction || 0

  // Standing orders
  const orders = d.standingOrders || []
  const totalMonthly = orders.reduce((sum, o) => sum + o.amount, 0)

  // Tax saved
  const taxSavedAnnual = d.totalTaxSavedAnnual || 0

  // Gap closed
  const gapClosed = d.totalGapClosed || 0

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.firmName}>Motion Accountancy</Text>
            <Text style={styles.docTitle}>Session 1 Summary — Financial Freedom Programme</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.clientName}>{name}</Text>
            <Text style={styles.clientDate}>{d.sessionDate || new Date().toLocaleDateString('en-GB')}</Text>
          </View>
        </View>

        {/* Freedom Number reveal */}
        <View style={styles.freedomBox}>
          <Text style={styles.freedomLabel}>Your Freedom Number</Text>
          <Text style={styles.freedomNumber}>{fmt(freedomNumberMonthly)}/month</Text>
          <Text style={styles.freedomSub}>
            Target freedom age: {freedomAge} — in today&apos;s money
          </Text>
        </View>

        {/* Key metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Session 1 — Key Figures</Text>
          <View style={styles.metricsGrid}>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Freedom age</Text>
              <Text style={styles.metricValue}>Age {freedomAge}</Text>
              <Text style={styles.metricSub}>Your target</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Annual tax saved</Text>
              <Text style={styles.metricValue}>{fmt(taxSavedAnnual)}</Text>
              <Text style={styles.metricSub}>From extraction strategy</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Gap closed</Text>
              <Text style={styles.metricValue}>{fmt(gapClosed)}</Text>
              <Text style={styles.metricSub}>Towards freedom number</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Salary</Text>
              <Text style={styles.metricValue}>{fmt(d.salary || 0)}/yr</Text>
              <Text style={styles.metricSub}>Annual gross</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Dividends</Text>
              <Text style={styles.metricValue}>{fmt(d.dividends || 0)}/yr</Text>
              <Text style={styles.metricSub}>Annual</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Pension balance</Text>
              <Text style={styles.metricValue}>{fmt(d.pensionBalance || 0)}</Text>
              <Text style={styles.metricSub}>Current pot</Text>
            </View>
          </View>
        </View>

        {/* Plan decisions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Plan Decisions</Text>
          {dividendReduction > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>Dividend reduction (employer pension)</Text>
              <Text style={styles.actionValue}>{fmt(dividendReduction)}/month → {fmt(pensionMonthly)}/month grossed up</Text>
            </View>
          )}
          {(d.planMortgageOverpayment || 0) > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>Mortgage overpayment</Text>
              <Text style={styles.actionValue}>{fmt(d.planMortgageOverpayment)}/month</Text>
            </View>
          )}
          {(d.planIsaMonthlyContribution || 0) > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>ISA contribution</Text>
              <Text style={styles.actionValue}>{fmt(d.planIsaMonthlyContribution)}/month</Text>
            </View>
          )}
        </View>

        {/* Standing orders */}
        {orders.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Standing Orders to Set Up</Text>
            <View style={styles.soBox}>
              <Text style={styles.soTitle}>Monthly Standing Orders</Text>
              {orders.map((o, i) => (
                <View key={i} style={styles.soRow}>
                  <Text style={styles.soLabel}>{o.label}</Text>
                  <Text style={styles.soValue}>{fmt(o.amount)}/mo</Text>
                </View>
              ))}
              <View style={{ ...styles.soRow, borderTopWidth: 1, borderTopColor: '#93c5fd', paddingTop: 4, marginTop: 4 }}>
                <Text style={{ ...styles.soLabel, fontFamily: 'Helvetica-Bold' }}>Total monthly</Text>
                <Text style={{ ...styles.soValue, color: '#1e40af' }}>{fmt(totalMonthly)}/mo</Text>
              </View>
            </View>
          </View>
        )}

        {/* Next steps */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Next Steps</Text>
          <View style={styles.actionRow}>
            <Text style={styles.actionLabel}>1. Set up standing orders above</Text>
            <Text style={styles.actionValue}>This week</Text>
          </View>
          <View style={styles.actionRow}>
            <Text style={styles.actionLabel}>2. Session 2 — Voyant modelling deep dive</Text>
            <Text style={styles.actionValue}>Booked</Text>
          </View>
          <View style={styles.actionRow}>
            <Text style={styles.actionLabel}>3. Confirm pension headroom with provider</Text>
            <Text style={styles.actionValue}>Before S2</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Motion Accountancy Ltd — {name} — Session 1 Summary</Text>
          <Text style={styles.footerText}>{new Date().toLocaleDateString('en-GB')}</Text>
        </View>
        <Text style={styles.disclaimer}>
          This document is a summary of decisions made during Session 1 of the Financial Freedom Programme. It does not constitute regulated financial advice. All projections are approximations for planning purposes only and are not guaranteed. Motion Accountancy Ltd is not authorised to provide regulated financial advice. Please consult a qualified financial adviser for personal recommendations.
        </Text>

      </Page>
    </Document>
  )
}
